/**
 * Static (design-only) data for the Advisor screen.
 * Replace these with the real endpoints once the Advisor APIs are available.
 */

/** A gap rule as returned by /data_collection/advisor/gap_rules/. */
export interface AdvisorGapRule {
  id: number;
  rule_title: string;
  /** the canonical metric this rule fills in */
  target_field: string;
  expression_sql: string | null;
  expression: any;
  is_active: boolean | null;
  destination_table_id: number;
  destination_table: string | null;
  description: string | null;
  data_source: string | null;
  /** comma separated metric names, e.g. "total_income,cpa_income" */
  dependency_field: string | null;
  maps_using: number;
  created: string | null;
  modified: string | null;
}

export interface AdvisorGapRuleOption {
  id: number;
  /** the canonical metric this rule can fill in */
  targetMetric: string;
  /** rule label as shown in the dropdown, e.g. "rule 26 · ftd" */
  label: string;
  expression: string;
  description: string;
  dependsOn: string[];
}

export const advisorInterfaceOptions = [
  { id: 1, label: "IncomeAccess" },
  { id: 2, label: "NetRefer" },
  { id: 3, label: "MyAffiliates" },
  { id: 4, label: "Affilka" },
  { id: 5, label: "Cellxpert" },
];

export const advisorDestinationTableOptions = [
  { id: 1, label: "monthly_brand_performance" },
  { id: 2, label: "daily_brand_performance" },
  { id: 4, label: "daily_user_performance" },
];

/** Canonical metrics listed on every Advisor map screen. */
export const advisorCanonicalMetrics = [
  "ftd",
  "qftd",
  "cpa_income",
  "revshare_income",
  "net_revenue",
  "total_income",
];

/**
 * Gap rules available for the selected destination. Each rule targets a single
 * canonical metric - the rest of the metrics can only come from the source.
 */
export const advisorGapRuleOptions: AdvisorGapRuleOption[] = [
  {
    id: 26,
    targetMetric: "qftd",
    label: "rule 26 · ftd",
    expression: "ftd",
    description: "computes ftd",
    dependsOn: ["ftd"],
  },
  {
    id: 24,
    targetMetric: "cpa_income",
    label: "rule 24 · qftd * cpa_deal",
    expression: "qftd * cpa_deal",
    description: "multiplies qftd by cpa_deal",
    dependsOn: ["qftd"],
  },
  {
    id: 19,
    targetMetric: "revshare_income",
    label: "rule 19 · MULTIPLY(net_revenue, revshare_deal)",
    expression: "MULTIPLY(net_revenue, revshare_deal)",
    description: "multiplies net_revenue by revshare_deal",
    dependsOn: ["net_revenue"],
  },
  {
    id: 29,
    targetMetric: "total_income",
    label: "rule 29 · cpa_income + revshare_income",
    expression: "cpa_income + revshare_income",
    description: "adds cpa_income and revshare_income",
    dependsOn: ["cpa_income", "revshare_income"],
  },
];

/**
 * Gap rules already assigned on an existing map - drives both the read-only
 * Map Details screen and the pre-filled Edit Map form.
 */
export const advisorAssignedGapRules: Record<string, number | null> = {
  ftd: null,
  qftd: 26,
  cpa_income: 24,
  revshare_income: 19,
  net_revenue: null,
  total_income: 29,
};

export const getAdvisorGapRuleById = (id: number | null) =>
  advisorGapRuleOptions.find((rule) => rule.id === id) ?? null;

export const ADVISOR_GAP_RULE_PLACEHOLDER = "None - Use source Value";

export const ADVISOR_GAP_RULE_NOTE =
  "Each metric is taken from the source unless you assign a gap rule. Rules are filtered to this destination and matching target field.";

/** Fallbacks used when the screen is opened without the map query params. */
export const ADVISOR_MAP_DETAILS_DEFAULTS = {
  mapName: "NetRefer_daily_brand_performance_1",
  interface: "IncomeAccess",
  destinationTable: "monthly_brand_performance",
};
